import { Fragment, useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import TeamMemberModal, { MemberDialog } from "./TeamMemberModal.tsx";
import TeamItem from "./TeamItem.tsx";
import { engagementStore } from "../../engagementStore.ts";
import { EngagementService } from "@services/engagements/engagementService.ts";
import { teamMemberStore } from "./teamMemberStore.ts";
import { TeamMember } from "@models/teamMember.model.ts";
import { EmployeeService } from "@services/person/EmployeeService.ts";
import ModalDelete from "src/components/common/ModalDelete.tsx";
import { workflowEvents } from "../../workflow/workflowEvents.ts";
import "./team.scss";

const Team = () => {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const { clientTeamMembers, providerTeamMembers } = useSnapshot(teamMemberStore);

  const [dialog, setDialog] = useState<MemberDialog | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadContacts();
    loadMembers();
    loadRoles();
  }, [selectedEngagement.id]);

  async function loadContacts() {
    const service = new EmployeeService();
    const [provider, client] = await Promise.all([
      service.getEmployees(selectedEngagement.service_provider.id),
      service.getEmployees(selectedEngagement.client.id),
    ]);
    teamMemberStore.employees = provider;
    teamMemberStore.clientEmployees = client;
  }

  async function loadMembers() {
    const service = new EngagementService();
    const members = await service.getTeamMembers(selectedEngagement.id);

    teamMemberStore.clientTeamMembers = members.filter(
      (tm) => tm.employee.organization.id === selectedEngagement.client.id,
    );
    teamMemberStore.providerTeamMembers = members.filter(
      (tm) => tm.employee.organization.id === selectedEngagement.service_provider.id,
    );

    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
  }

  async function loadRoles() {
    const service = new EngagementService();
    const [client, provider] = await Promise.all([
      service.getTeamRoles("CLIENT"),
      service.getTeamRoles("PROVIDER"),
    ]);
    teamMemberStore.clientRoles = client;
    teamMemberStore.providerRoles = provider;
  }

  async function deleteMember() {
    if (!memberToDelete) return;
    setIsDeleting(true);
    try {
      const service = new EngagementService();
      await service.deleteTeamMember(memberToDelete.employee.id, selectedEngagement.id);
      await loadMembers();
    } finally {
      setIsDeleting(false);
      setMemberToDelete(null);
    }
  }

  /*
      One card per person, with their roles on the card. Grouping by role headed
      the list before, which showed anyone holding two roles as two identical
      cards under two headings.
  */
  const renderMembers = (members: TeamMember[]) => (
    <div className="team-grid">
      {members.map((member, idx) => (
        <div
          key={member.employee.id || member.employee.person.id || idx}
          className="team-grid-item"
        >
          <TeamItem
            teamMember={member}
            onEdit={(m) => setDialog({ mode: "edit", member: m })}
            onDelete={setMemberToDelete}
          />
        </div>
      ))}
    </div>
  );

  const renderSection = (
    label: string,
    icon: string,
    members: TeamMember[],
    side: "CLIENT" | "PROVIDER",
    emptyMessage: string,
  ) => (
    <div className="team-section">
      <div className="team-section__head">
        <i className={`${icon} text-muted`} />
        <span className="team-section__title">{label}</span>
        <span className="team-section__count">{members.length}</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary ms-auto"
          onClick={() => setDialog({ mode: "add", side })}
        >
          <i className="bi bi-plus-lg me-1" />
          Member
        </button>
      </div>
      <div className="team-section__body">
        {members.length === 0 ? (
          <div className="team-section__empty">{emptyMessage}</div>
        ) : (
          renderMembers(members)
        )}
      </div>
    </div>
  );

  return (
    <Fragment>
      <div className="team-page">
        <div className="team-page__head">
          <h5 className="mb-1">Team</h5>
          <p className="text-muted small mb-0">
            People working on this engagement, on both sides.
          </p>
        </div>

        <div className="team-page__sections">
          {renderSection(
            selectedEngagement.client?.name || "Client",
            "bi bi-person-badge",
            clientTeamMembers as TeamMember[],
            "CLIENT",
            "No client team members yet",
          )}

          {renderSection(
            selectedEngagement.service_provider?.name || "Internal",
            "bi bi-briefcase",
            providerTeamMembers as TeamMember[],
            "PROVIDER",
            "No internal team members yet",
          )}
        </div>
      </div>

      <TeamMemberModal
        dialog={dialog}
        onClose={() => setDialog(null)}
        onSaved={loadMembers}
      />

      <ModalDelete
        title="Team Member"
        objectLabel={
          memberToDelete
            ? `${memberToDelete.employee.person.first_name} ${memberToDelete.employee.person.last_name}`
            : ""
        }
        deleteFn={deleteMember}
        visible={memberToDelete !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setMemberToDelete(null);
        }}
        isLoading={isDeleting}
      />
    </Fragment>
  );
};

export default Team;
